import React, { useRef, useEffect } from 'react';

function getHighlightedText(text: string, limit: number) {
    if (!text) return { allowed: '', greyed: '' };
    let currentWords = 0;
    let splitIndex = text.length;
    let inWord = false;
    for (let i = 0; i < text.length; i++) {
        const char = text[i];
        const isWhitespace = /\s/.test(char);
        
        if (!isWhitespace) {
            if (!inWord) {
                currentWords++;
                inWord = true;
            }
        } else {
            inWord = false;
        }
        
        if (currentWords > limit) {
            splitIndex = i;
            break;
        }
    }
    return {
        allowed: text.slice(0, splitIndex),
        greyed: text.slice(splitIndex)
    };
}

export function PeelHighlight({ value, onChange, wordLimit, disabled }: any) {
    const divRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    
    const { allowed, greyed } = getHighlightedText(value, wordLimit);
    
    // Sync scroll
    const handleScroll = () => {
        if (divRef.current && textareaRef.current) {
            divRef.current.scrollTop = textareaRef.current.scrollTop;
            divRef.current.scrollLeft = textareaRef.current.scrollLeft;
        }
    };

    // Use a ResizeObserver to sync dimensions if textarea resizes
    useEffect(() => {
        if (!textareaRef.current || !divRef.current) return;
        const resizeObserver = new ResizeObserver(() => {
             if (divRef.current && textareaRef.current) {
                 divRef.current.style.height = textareaRef.current.offsetHeight + 'px';
                 divRef.current.style.width = textareaRef.current.offsetWidth + 'px';
             }
        });
        resizeObserver.observe(textareaRef.current);
        return () => resizeObserver.disconnect();
    }, []);

    return (
        <div className="relative w-full">
            {/* The background visually showing the text */}
            <div 
                ref={divRef}
                className="absolute top-0 left-0 w-full h-[240px] bg-white dark:bg-[#181a1f] border border-zinc-300 dark:border-[#3e424d] rounded-xl px-4 py-4 text-[14px] leading-relaxed whitespace-pre-wrap break-words overflow-hidden pointer-events-none z-0 shadow-inner"
                style={{ fontFamily: 'inherit' }}
            >
                <span className="text-zinc-900 dark:text-white">{allowed}</span>
                {greyed && (
                    <span className="text-zinc-400 dark:text-zinc-600 bg-zinc-100 dark:bg-[#272a31]">{greyed}</span>
                )}
                {/* Hack to ensure trailing newlines are rendered so scrolling matches exactly */}
                {value.endsWith('\n') && <br/>}
                <br/>
            </div>
            
            {/* The transparent interactive textarea */}
            <textarea
                ref={textareaRef}
                value={value}
                onChange={onChange}
                onScroll={handleScroll}
                disabled={disabled}
                placeholder="Enter your topic, prompt, or raw evidence here..."
                className="relative w-full min-h-[240px] h-[240px] bg-transparent text-transparent caret-zinc-900 dark:caret-white border border-transparent rounded-xl px-4 py-4 text-[14px] leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-green-600/50 transition-shadow custom-scrollbar z-10"
                style={{ fontFamily: 'inherit' }}
                spellCheck="false"
            />
        </div>
    );
}
